import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { NotificationTypes } from '../models/notifications/notification.model';
import { NotificationSaleAdminModel, NotificationSaleAdmin } from '../models/notifications/notification-sale-admin.model';
import { NotificationVet } from '../models/notifications/notification-vet.model';
import { SpinnerService } from '../shared-new/services/spinner.service';
import { NotificationType, NotificationTypeDto } from '../shared-new/enums/notification-type.enum';
import { LookupModel } from '../models/common/lookup.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  public baseUrl = environment.api2BaseUrl;
  public authBaseUrl = environment.apiAuthUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(private readonly http: HttpClient, private spinnerService: SpinnerService) { } // Inject SpinnerService

  public getNotificationTypes(): Observable<NotificationTypes[]> {
    this.spinnerService.addRequest('getNotificationTypes'); // Show spinner
    return this.http.get<NotificationTypes[]>(`${this.baseUrl}/api/Notifications/NotificationTypes`).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getNotificationTypes')) // Hide spinner on completion
    );
  }

  public getSaleAdminNotification(): Observable<NotificationSaleAdmin[]> {
    this.spinnerService.addRequest('getSaleAdminNotification'); // Show spinner
    return this.http.get<NotificationSaleAdmin[]>(`${this.baseUrl}/api/Notifications/SaleMember`).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getSaleAdminNotification')) // Hide spinner on completion
    );
  }

  public getNotifySaleMember(notificationTypeId: number): Observable<any> {
    this.spinnerService.addRequest('getMemberNotification'); // Show spinner
    return this.http.get<any>(`${this.baseUrl}/api/Notifications/SaleMember/${notificationTypeId}`).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getMemberNotification')) // Hide spinner on completion
    );
  }  

  public postSaleAdminNotification(notificationSaleAdminModel: NotificationSaleAdminModel): Observable<NotificationSaleAdminModel> {
    this.spinnerService.addRequest('postSaleAdminNotification'); // Show spinner
    return this.http
      .post<NotificationSaleAdminModel>(`${this.baseUrl}/api/Notifications/SaleMember`, notificationSaleAdminModel)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('postSaleAdminNotification')) // Hide spinner on completion
      );
  }

  public getVetNotifications(userId?: number): Observable<NotificationVet[]> {
    this.spinnerService.addRequest('getVetNotifications'); // Show spinner

    // If userId is provided, add it to the URL, otherwise fallback to default /User
    const url = userId
      ? `${this.baseUrl}/api/Notifications/${userId}/User`
      : `${this.baseUrl}/api/Notifications/null/User`;

    return this.http.get<NotificationVet[]>(url).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(() => error);
      }),
      finalize(() => this.spinnerService.popRequest('getVetNotifications')) // Hide spinner
    );
  }


  public getVetMemberUsers(memberIds: string, type: NotificationType = NotificationType.Vet_Notification): Observable<any> {
    this.spinnerService.addRequest('getVetMemberUsers'); // Show spinner
    const model = {
      memberIds: memberIds,
      notificationType: type
    };
    return this.http
      .post<any>(`${this.baseUrl}/api/Notifications/VetMemberUsers`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getVetMemberUsers')) // Hide spinner on completion
      );
  }

  public getStudySubmissionRecipients(saleId: number, horseId: number, statusId: number, studyFileIds: number[]): Observable<any> {
    this.spinnerService.addRequest('getStudySubmissionRecipients'); // Show spinner
    return this.http.post<any>(`${this.baseUrl}/api/Notifications/${saleId}/${horseId}/${statusId}/StudyRecipients`, studyFileIds).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getStudySubmissionRecipients')) // Hide spinner on completion
    );
  }

  public getNotifyVetInfo(memberId: number, statusId: number,saleId: number): Observable<any> {
    this.spinnerService.addRequest('getNotifyVetInfo'); // Show spinner
    return this.http.post<any>(`${this.baseUrl}/api/Notifications/${memberId}/${statusId}/${saleId}/NotifyVetInfo`, null).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getNotifyVetInfo')) // Hide spinner on completion
    );
  }

  public updateVetNotifications(notificationVet: NotificationVet[], userId?: number): Observable<any> {
    this.spinnerService.addRequest('updateVetNotifications'); // Show spinner

    const url = userId
      ? `${this.baseUrl}/api/Notifications/${userId}/User`
      : `${this.baseUrl}/api/Notifications/null/User`;

    return this.http
      .post<boolean>(url, notificationVet)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('updateVetNotifications')) // Hide spinner on completion
      );
  }


  public deleteNotificationContact(notificationContactId: number): Observable<Boolean> {
    this.spinnerService.addRequest('deleteNotificationContact'); // Show spinner
    return this.http
      .delete<Boolean>(`${this.baseUrl}/api/Notifications/${notificationContactId}/User`)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteNotificationContact')) // Hide spinner on completion
      );
  }

  public updateVetMobileAndEmail(notificationVet: NotificationVet): Observable<Boolean> {
    this.spinnerService.addRequest('updateVetMobileAndEmail'); // Show spinner
    return this.http
      .post<Boolean>(`${this.authBaseUrl}/members/update/mobile`, notificationVet)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateVetMobileAndEmail')) // Hide spinner on completion
      );
  }

  public getAllNotificationTypes(): Observable<NotificationTypeDto[]> {
    this.spinnerService.addRequest('getAllNotificationTypes'); // Show spinner
    return this.http
      .get<NotificationTypeDto[]>(`${this.baseUrl}/api/NotificationType/types`)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message); // Assuming onError is a Subject in the service; adjust if needed
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getAllNotificationTypes')) // Hide spinner on completion
      );
  }

  public getNotificationTypeById(id:number): Observable<NotificationTypeDto> {
    this.spinnerService.addRequest('getNotificationTypeById'); // Show spinner
    return this.http
      .get<NotificationTypeDto>(`${this.baseUrl}/api/NotificationType/${id}/type`)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message); // Assuming onError is a Subject in the service; adjust if needed
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getNotificationTypeById')) // Hide spinner on completion
      );
  }

  public updateNotificationType(notificationType: NotificationTypeDto): Observable<NotificationTypeDto> {
    this.spinnerService.addRequest('updateNotificationType'); // Show spinner
    return this.http
      .post<NotificationTypeDto>(`${this.baseUrl}/api/NotificationType/type`, notificationType)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message); // Assuming onError is a Subject in the service; adjust if needed
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateNotificationType')) // Hide spinner on completion
      );
  }

  public getNotificationGroups(): Observable<LookupModel[]> {
    this.spinnerService.addRequest('getNotificationGroups'); // Show spinner
    return this.http
      .get<LookupModel[]>(`${this.baseUrl}/api/NotificationType/groups`)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message); // Assuming onError is a Subject in the service; adjust if needed
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getNotificationGroups')) // Hide spinner on completion
      );
  }
}
